<template>
  <div
    :id="uuid"
    :style="{
      'scroll-margin-top': pageEditorStore.scrollMarginTop,
    }"
    class="relative"
  >
    <HeroContent
      v-if="!hasResponsiveOverrides"
      :form-data="formData"
      :block-id="uuid"
    />
    <template v-else>
      <HeroResponsiveView
        :form-data="mobileView"
        :block-id="uuid"
        visibility-class="sm:hidden"
      />
      <HeroResponsiveView
        :form-data="mediumView"
        :block-id="uuid"
        visibility-class="hidden sm:block lg:hidden"
      />
      <HeroResponsiveView
        :form-data="desktopView"
        :block-id="uuid"
        visibility-class="hidden lg:block"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import type {
  CoverImageRatioMode,
  CoverLogoAlign,
  CoverPaddingSize,
  CoverTextAlign,
} from '../cover/types'
import type { HeroDisplayData, HeroFormData, HeroFontSize } from './types'
import { getHeroGoogleFontHref } from './google-font'
import HeroContent from './HeroContent.vue'
import HeroResponsiveView from './HeroResponsiveView.vue'
import { usePageEditor } from '~/stores/page-editor'

interface PropsInterface {
  uuid: string
  formData: HeroFormData
}

interface ResponsiveSettings {
  isEnabled: boolean
  hasBgImage: boolean
  bgImage?: string
  hasBgColor: boolean
  bgColor?: string
  imageRatioMode?: CoverImageRatioMode
  paddingSize?: CoverPaddingSize
  logoWidth?: number
  logoAlign?: CoverLogoAlign
  maxWidth?: number
  sloganMaxWidth?: number
  sloganTextAlign?: CoverTextAlign
  sloganFontSize?: HeroFontSize
  textFontSize?: HeroFontSize
  textAlign?: CoverTextAlign
}

const props = defineProps<PropsInterface>()
const pageEditorStore = usePageEditor()

const googleFontHref = computed(() =>
  pageEditorStore.isInIframe ? undefined : getHeroGoogleFontHref(props.formData)
)

useHead(() => ({
  link: googleFontHref.value
    ? [
        {
          key: `hero-google-font-${props.uuid}`,
          rel: 'stylesheet',
          href: googleFontHref.value,
        },
      ]
    : [],
}))

const desktopView = computed<HeroDisplayData>(() => props.formData)

const hasResponsiveOverrides = computed(
  () =>
    props.formData.overrideMobileView === true ||
    props.formData.overrideMediumView === true
)

const hasOwnProperty = (property: keyof HeroFormData) =>
  Object.prototype.hasOwnProperty.call(props.formData, property)

const resolveImageRatio = (mode?: CoverImageRatioMode) => {
  if (mode === 'enabled') {
    return true
  }
  if (mode === 'disabled') {
    return false
  }
  return props.formData.useImageRatio ?? false
}

const resolveResponsiveView = (
  settings: ResponsiveSettings
): HeroDisplayData => {
  if (!settings.isEnabled) {
    return props.formData
  }

  let bgImage = props.formData.bgImage
  let bgColor = props.formData.bgColor

  if (settings.hasBgImage) {
    bgImage = settings.bgImage || undefined
    bgColor = settings.bgColor ?? bgColor
  } else if (settings.hasBgColor) {
    bgImage = undefined
    bgColor = settings.bgColor
  }

  return {
    ...props.formData,
    bgImage,
    bgColor,
    useImageRatio: resolveImageRatio(settings.imageRatioMode),
    paddingSize:
      props.formData.useImageRatio === true &&
      settings.imageRatioMode === 'disabled'
        ? settings.paddingSize ?? props.formData.paddingSize
        : props.formData.paddingSize,
    logoWidth: settings.logoWidth ?? props.formData.logoWidth,
    logoAlign: settings.logoAlign ?? props.formData.logoAlign,
    maxWidth: settings.maxWidth ?? props.formData.maxWidth,
    sloganMaxWidth: settings.sloganMaxWidth ?? props.formData.sloganMaxWidth,
    sloganTextAlign: settings.sloganTextAlign ?? props.formData.sloganTextAlign,
    sloganFontSize: settings.sloganFontSize ?? props.formData.sloganFontSize,
    textFontSize: settings.textFontSize ?? props.formData.textFontSize,
    textAlign: settings.textAlign ?? props.formData.textAlign,
  }
}

const mobileView = computed<HeroDisplayData>(() =>
  resolveResponsiveView({
    isEnabled: props.formData.overrideMobileView === true,
    hasBgImage: hasOwnProperty('mobileBgImage'),
    bgImage: props.formData.mobileBgImage,
    hasBgColor: hasOwnProperty('mobileBgColor'),
    bgColor: props.formData.mobileBgColor,
    imageRatioMode: props.formData.mobileImageRatioMode,
    paddingSize: props.formData.mobilePaddingSize,
    logoWidth: props.formData.mobileLogoWidth,
    logoAlign: props.formData.mobileLogoAlign,
    maxWidth: props.formData.mobileMaxWidth,
    sloganMaxWidth: props.formData.mobileSloganMaxWidth,
    sloganTextAlign: props.formData.mobileSloganTextAlign,
    sloganFontSize: props.formData.mobileSloganFontSize,
    textFontSize: props.formData.mobileTextFontSize,
    textAlign: props.formData.mobileTextAlign,
  })
)

const mediumView = computed<HeroDisplayData>(() =>
  resolveResponsiveView({
    isEnabled: props.formData.overrideMediumView === true,
    hasBgImage: hasOwnProperty('mediumBgImage'),
    bgImage: props.formData.mediumBgImage,
    hasBgColor: hasOwnProperty('mediumBgColor'),
    bgColor: props.formData.mediumBgColor,
    imageRatioMode: props.formData.mediumImageRatioMode,
    paddingSize: props.formData.mediumPaddingSize,
    logoWidth: props.formData.mediumLogoWidth,
    logoAlign: props.formData.mediumLogoAlign,
    maxWidth: props.formData.mediumMaxWidth,
    sloganMaxWidth: props.formData.mediumSloganMaxWidth,
    sloganTextAlign: props.formData.mediumSloganTextAlign,
    textAlign: props.formData.mediumTextAlign,
  })
)
</script>
